import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { adminSubscribersService } from '@/services/admin-subscribers.service';
import { ApiError } from '@/services/http';
import type { SubscriberListView } from '@/types/api';

interface AddSubscriberDialogProps {
  open: boolean;
  lists: SubscriberListView[];
  onClose: () => void;
  onCreated: () => void;
}

/** Manual single-subscriber add. POSTs to the existing create endpoint. */
export function AddSubscriberDialog({ open, lists, onClose, onCreated }: AddSubscriberDialogProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [listId, setListId] = useState('');
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setListId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || saving) return;
    setSaving(true);
    try {
      await adminSubscribersService.create({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        listId: listId || null,
      });
      toast.success('Subscriber added.');
      reset();
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add subscriber.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-or-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-or-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[18px] font-bold text-or-slate-900">Add subscriber</h2>
        <p className="mt-1 text-[12px] text-or-slate-500">
          Adds one subscriber to your audience. Email must be unique.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <Field label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="parent@example.com"
              className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </Field>
            <Field label="Last name">
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </Field>
          </div>
          <Field label="Phone (optional)">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            />
          </Field>
          <Field label="List (optional)">
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            >
              <option value="">Unassigned</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={!email.trim() || saving}>
            {saving ? 'Adding…' : 'Add subscriber'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
      {label}
      <span className="font-normal normal-case">{children}</span>
    </label>
  );
}
